
import { Era } from "@/types/investigator";

export interface EraTableConfig {
  occupations: string;
  skills: string;
  weapons: string;
}

export interface SocialClassOption {
  value: string;
  label: string;
  cr: string;
  description: string;
}

export interface EraSocialClassConfig {
  type: 'class' | 'band';
  fieldLabel: string;
  switchLabel: string;
  switchDescription: string;
  tooltip: string;
  options: SocialClassOption[];
  aristocracyWarning?: string;
}

export interface EraConfig {
  id: Era;
  route: string;
  title: string;
  subtitle: string;
  titleTooltip: string;
  seo: {
    title: string;
    description: string;
  };
  tables: EraTableConfig;
  heroEffect: 'bokeh' | 'spotlight' | 'rain' | 'candlelight' | 'dust' | 'embers' | null;
  socialClassSystem: EraSocialClassConfig | null;
  /** JSON import key for weapons data (used in SelectedWeaponsList / CharacterSheetEquipment) */
  weaponsDataKey: string;
  fallbackOccupations: string[];
  /** Whether this era supports the Pulp Cthulhu ruleset overlay. */
  supportsPulp?: boolean;
  /** When true, the Pulp overlay is on by default and cannot be turned off (e.g. the 1930s Pulp era). */
  pulpAlwaysOn?: boolean;
}

const SHARED_FALLBACK_OCCUPATIONS = [
  "Antiquarian", "Artist", "Author", "Detective", "Doctor",
  "Engineer", "Journalist", "Lawyer", "Librarian", "Musician",
  "Occultist", "Police Detective", "Private Investigator", "Professor", "Scientist",
];

export const ERA_REGISTRY: Record<Era, EraConfig> = {
  '1920s': {
    id: '1920s',
    route: '/1920s',
    title: 'The Roaring Twenties',
    subtitle: 'Record of Prior Existence',
    titleTooltip: 'Your occupation determines your professional skills, and age can affect your characteristics.',
    seo: {
      title: 'Investigator Details',
      description: 'Start building your Call of Cthulhu 7th Edition investigator — enter name, occupation, age, and background details.',
    },
    tables: {
      occupations: 'occupation_database',
      skills: 'skills',
      weapons: 'weapon_table',
    },
    heroEffect: 'bokeh',
    socialClassSystem: null,
    weaponsDataKey: '1920s',
    fallbackOccupations: SHARED_FALLBACK_OCCUPATIONS,
    supportsPulp: true,
  },
  gaslight: {
    id: 'gaslight',
    route: '/gaslight',
    title: 'Gaslight 1890s',
    subtitle: 'A Victorian Investigation',
    titleTooltip: 'In the Gaslight era, your social class determines which occupations are available to you.',
    seo: {
      title: 'Gaslight Investigator Details',
      description: 'Create a Gaslight (1890s) era investigator for Call of Cthulhu 7th Edition.',
    },
    tables: {
      occupations: 'gaslight_occupations',
      skills: 'gaslight_skills',
      weapons: 'gaslight_weapons',
    },
    heroEffect: 'spotlight',
    socialClassSystem: {
      type: 'class',
      fieldLabel: 'Social Class',
      switchLabel: 'Restrict by class',
      switchDescription: 'Filter occupations to the selected social class only',
      tooltip: 'Your social standing determines which occupations are available and constrains your Credit Rating.',
      options: [
        { value: 'lower', label: 'Lower Class', cr: 'CR 00–09', description: 'Laborers, street-folk, the impoverished.' },
        { value: 'middle', label: 'Middle Class', cr: 'CR 10–49', description: 'Professionals, tradesmen, shopkeepers.' },
        { value: 'upper', label: 'Upper Class', cr: 'CR 50–99', description: 'Aristocracy, landed gentry, the idle rich.' },
      ],
    },
    weaponsDataKey: 'gaslight',
    fallbackOccupations: [],
    supportsPulp: true,
  },
  modern: {
    id: 'modern',
    route: '/modern',
    title: 'The Modern Age',
    subtitle: 'Digital Devilry',
    titleTooltip: 'Your occupation determines your professional skills, and age can affect your characteristics.',
    seo: {
      title: 'Investigator Details — Modern',
      description: 'Create a present-day Call of Cthulhu 7th Edition investigator with modern occupations, skills, and equipment.',
    },
    tables: {
      occupations: 'modern_occupations',
      skills: 'modern_skills',
      weapons: 'modern_weapons',
    },
    heroEffect: 'rain',
    socialClassSystem: null,
    weaponsDataKey: 'modern',
    fallbackOccupations: SHARED_FALLBACK_OCCUPATIONS,
    supportsPulp: true,
  },
  regency: {
    id: 'regency',
    route: '/regency',
    title: 'Regency 1810s',
    subtitle: 'A Dance with Darkness',
    titleTooltip: 'In the Regency era, your Occupational Band determines which occupations are available and constrains your Credit Rating.',
    seo: {
      title: 'Regency Investigator Details',
      description: 'Create a Regency (1810s) era investigator for Call of Cthulhu 7th Edition.',
    },
    tables: {
      occupations: 'regency_occupations',
      skills: 'regency_skills',
      weapons: 'regency_weapons',
    },
    heroEffect: 'candlelight',
    socialClassSystem: {
      type: 'band',
      fieldLabel: 'Occupational Band',
      switchLabel: 'Restrict by band',
      switchDescription: 'Filter occupations to the selected band only',
      tooltip: 'Your Occupational Band determines which occupations are available and constrains your Credit Rating. Bands reflect the rigid social hierarchy of Regency England.',
      options: [
        { value: '1', label: 'Laborer / Servant', cr: 'CR 09–30', description: 'Soldiers, servants, smugglers, and the working poor.' },
        { value: '2', label: 'Shopkeeper / Craftsperson', cr: 'CR 20–60', description: 'Actors, farmers, musicians, traders, and skilled artisans.' },
        { value: '3', label: 'Professional', cr: 'CR 30–70', description: 'Doctors, lawyers, professors, military officers.' },
        { value: '4', label: 'Gentry / Nouveau Riche', cr: 'CR 50–90', description: 'Gentlemen, gentlewomen, and the newly wealthy.' },
        { value: '5', label: 'Aristocracy', cr: 'CR 90–100', description: 'The highest echelons of society. Not routinely available.' },
      ],
      aristocracyWarning: 'Aristocracy is not routinely available — consult your Keeper before proceeding. Showing Gentry occupations as a starting point.',
    },
    weaponsDataKey: 'regency',
    fallbackOccupations: [],
    supportsPulp: true,
  },
  western: {
    id: 'western',
    route: '/western',
    title: 'The Old West',
    subtitle: 'Down Darker Trails',
    titleTooltip: 'In the Old West, frontier life shapes your skills — riding, shooting, and surviving the wilderness all play a part.',
    seo: {
      title: 'Western Investigator Details',
      description: 'Create a frontier-era (1865–1895) investigator for Call of Cthulhu 7th Edition using Down Darker Trails.',
    },
    tables: {
      occupations: 'western_occupations',
      skills: 'western_skills',
      weapons: 'western_weapons',
    },
    heroEffect: 'dust',
    socialClassSystem: null,
    weaponsDataKey: 'western',
    fallbackOccupations: [
      'Cowboy', 'Drifter', 'Gambler', 'Gunfighter', 'Lawman',
      'Outlaw', 'Preacher', 'Prospector', 'Rancher', 'Saloon Keeper',
      'Scout', 'Sheriff', 'Soldier', 'Town Doctor', 'Trapper',
    ],
    supportsPulp: true,
  },
  darkages: {
    id: 'darkages',
    route: '/dark-ages',
    title: 'The Dark Ages',
    subtitle: 'Anglo-Saxon Shadows',
    titleTooltip: 'In the Dark Ages, your station — ceorl, thegn, or ealdorman — shapes which occupations are open to you.',
    seo: {
      title: 'Dark Ages Investigator Details',
      description: 'Create a Cthulhu Dark Ages (950–1050 CE) investigator for Call of Cthulhu 7th Edition with era-specific occupations, skills, and weapons.',
    },
    tables: {
      occupations: 'darkages_occupations',
      skills: 'darkages_skills',
      weapons: 'darkages_weapons',
    },
    heroEffect: 'embers',
    socialClassSystem: {
      type: 'band',
      fieldLabel: 'Social Standing',
      switchLabel: 'Restrict by standing',
      switchDescription: 'Filter occupations to the selected social standing only',
      tooltip: 'Your social standing determines which occupations are available and constrains your Status. The Anglo-Saxon hierarchy ran from ceorl to ealdorman.',
      options: [
        { value: '1', label: 'Ceorl / Thrall', cr: 'Status 0–30', description: 'Free commoners, beggars, hunters, mercenaries, and the unfree.' },
        { value: '2', label: 'Thegn / Townfolk', cr: 'Status 9–65', description: 'Craftspeople, guards, healers, sailors, free warriors, minstrels.' },
        { value: '3', label: 'Ealdorman / Clergy', cr: 'Status 9–99', description: 'Priests, monastics, scholars, household officers, lordly stewards.' },
      ],
    },
    weaponsDataKey: 'darkages',
    fallbackOccupations: [
      'Beggar', 'Cleric', 'Craftsperson', 'Free Farmer', 'Free Warrior',
      'Guard', 'Healer', 'Hermit / Heretic', 'Household Officer',
      'Juggler / Minstrel', 'Mercenary / Brigand', 'Merchant', 'Monk / Nun',
      'Pilgrim', 'Priest', 'Sailor', 'Scholar', 'Sergeant / Mayor',
      'Woodsman / Fisherman',
    ],
    supportsPulp: true,
  },
  invictus: {
    id: 'invictus',
    route: '/invictus',
    title: 'Cthulhu Invictus',
    subtitle: 'Shadows of the Roman Empire',
    titleTooltip: 'In Cthulhu Invictus, your Roman social standing — from slave to patrician — determines which occupations are open to you and constrains your Social Standing (Credit Rating).',
    seo: {
      title: 'Invictus Investigator Details',
      description: 'Create a Cthulhu Invictus (Imperial Rome, c. 100 CE) investigator for Call of Cthulhu 7th Edition with era-specific occupations, skills, and weapons.',
    },
    tables: {
      occupations: 'invictus_occupations',
      skills: 'invictus_skills',
      weapons: 'invictus_weapons',
    },
    heroEffect: null,
    socialClassSystem: {
      type: 'band',
      fieldLabel: 'Social Standing',
      switchLabel: 'Restrict by standing',
      switchDescription: 'Filter occupations to the selected Roman social band only',
      tooltip: 'Roman society ran from slaves at the bottom to the patrician families of the Senate. Your social band determines which occupations are open and constrains your Social Standing.',
      options: [
        { value: '1', label: 'Slave / Peregrinus', cr: 'Standing 0–15', description: 'Slaves, auxiliary non-citizens, frontier dwellers.' },
        { value: '2', label: 'Freedman / Plebeian', cr: 'Standing 5–40', description: 'Freed slaves, craftspeople, watchmen, small traders.' },
        { value: '3', label: 'Citizen / Equestrian', cr: 'Standing 9–65', description: 'Legionaries, merchants, scholars, physicians, centurions.' },
        { value: '4', label: 'Equestrian / Magistrate', cr: 'Standing 30–80', description: 'Priests, augurs, magistrates, equestrian officers.' },
        { value: '5', label: 'Patrician / Senator', cr: 'Standing 70–99', description: 'Senatorial families. Not routinely available without Keeper approval.' },
      ],
      aristocracyWarning: 'Patrician/Senator standing is not routinely available — consult your Keeper before proceeding. Showing Equestrian occupations as a starting point.',
    },
    weaponsDataKey: 'invictus',
    fallbackOccupations: [
      'Legionary', 'Centurion', 'Gladiator', 'Senator', 'Slave',
      'Freedman (Libertus)', 'Merchant (Mercator)', 'Scholar (Grammaticus)',
      'Physician (Medicus)', 'Priest (Sacerdos)', 'Augur', 'Vigil (Watchman)',
      'Craftsperson (Artifex)', 'Farmer (Agricola)', 'Auxiliary Scout', 'Sailor (Nauta)',
    ],
    supportsPulp: true,
  },
  pulp: {
    id: 'pulp',
    route: '/pulp-1930s',
    title: 'Pulp Cthulhu — 1930s',
    subtitle: 'Two-Fisted Action Against the Mythos',
    titleTooltip:
      'Pulp Cthulhu heroes are tougher and more capable. Choose an archetype and two talents to define your hero.',
    seo: {
      title: 'Pulp Cthulhu Investigator Details',
      description:
        'Create a 1930s Pulp Cthulhu hero — pulp archetypes, talents, and two-fisted adventure against the Mythos.',
    },
    tables: {
      // Pulp 1930s reuses the 1920s tables as a baseline; pulp-specific
      // occupations layer on top via src/data/pulp/pulp-occupations.ts.
      occupations: 'occupation_database',
      skills: 'skills',
      weapons: 'weapon_table',
    },
    heroEffect: 'bokeh',
    socialClassSystem: null,
    weaponsDataKey: '1920s',
    fallbackOccupations: SHARED_FALLBACK_OCCUPATIONS,
    supportsPulp: true,
    pulpAlwaysOn: true,
  },
};

/** Get the era config, falling back to 1920s */
export function getEraConfig(era: Era | string): EraConfig {
  return ERA_REGISTRY[era as Era] || ERA_REGISTRY['1920s'];
}

/** Get a list of all era routes for router config */
export function getAllEraRoutes(): string[] {
  return Object.values(ERA_REGISTRY).map(e => e.route);
}
